
import React, { useState, useRef, useEffect } from 'react';
import { Camera, RefreshCw, Check, Download, Layers, Eye, ArrowDown, ArrowUp, ZoomIn, Box, Palette, Aperture, Sun, Sliders, RectangleHorizontal, Monitor, Clapperboard, Sparkles } from 'lucide-react';
import Button from './Button';
import SettingsAccordion from './SettingsAccordion';
import { AestheticStyle, Product, StudioSettings, CameraAngle, ImageModel } from '../types';
import { generateVirtualPhotography, generateBatchPhotography, hasValidApiKey } from '../services/geminiService';
import { DEFAULT_STUDIO_SETTINGS } from '../config/defaults';
import { readFileAsBase64, getBase64Data, getMimeTypeFromBase64 } from '../utils/fileHelpers';

interface VirtualStudioProps {
  activeProduct: Product | null;
  onSave: (url: string, style: string) => void;
}

const ANGLES: { id: CameraAngle; label: string; icon: React.ElementType }[] = [
    { id: 'front', label: 'Front', icon: Eye },
    { id: 'isometric', label: 'Isometric', icon: Box },
    { id: 'top-down', label: 'Top-Down', icon: ArrowDown },
    { id: 'low-angle', label: 'Low Angle', icon: ArrowUp },
    { id: 'macro', label: 'Macro', icon: ZoomIn },
    { id: 'side', label: 'Side', icon: RectangleHorizontal },
];

const VirtualStudio: React.FC<VirtualStudioProps> = ({ activeProduct, onSave }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [style, setStyle] = useState<AestheticStyle>(AestheticStyle.STUDIO_MINIMAL);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [model, setModel] = useState<ImageModel>(ImageModel.NANO_BANANA);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [mode, setMode] = useState<'single' | 'batch'>('batch'); // Default to batch as per screenshot emphasis
  const [batchSize, setBatchSize] = useState(4);
  
  // Custom Settings
  const [showAdvanced, setShowAdvanced] = useState(true); // Default open to show controls
  const [settings, setSettings] = useState<StudioSettings>(DEFAULT_STUDIO_SETTINGS[AestheticStyle.STUDIO_MINIMAL]);
  const [customInstruction, setCustomInstruction] = useState('');

  // Accordion State
  const [openSection, setOpenSection] = useState<string | null>('style');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize from active product if available
  useEffect(() => {
    if (activeProduct && activeProduct.images.length > 0) {
        setSelectedImage(activeProduct.images[0].url);
    }
  }, [activeProduct]);

  // Update settings when style changes
  useEffect(() => {
    setSettings(DEFAULT_STUDIO_SETTINGS[style]);
  }, [style]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const base64 = await readFileAsBase64(file);
        setSelectedImage(base64);
        setResultImage(null);
        setBatchResults([]);
      } catch (e) {
        console.error("File reading failed", e);
      }
    }
  };

  const handleGenerate = async () => {
    if (!selectedImage) return;

    if (!hasValidApiKey()) {
        alert("API Key is missing. Please check your Vercel Environment Variables.");
        return;
    }

    setIsGenerating(true);
    setResultImage(null);
    setBatchResults([]);
    setBatchProgress(0);

    try {
      const base64Data = getBase64Data(selectedImage);
      const mimeType = getMimeTypeFromBase64(selectedImage);
      
      if (mode === 'single') {
        const result = await generateVirtualPhotography(
            base64Data, 
            style, 
            aspectRatio, 
            settings,
            mimeType,
            customInstruction,
            model
        );
        setResultImage(result);
      } else {
        const results = await generateBatchPhotography(
            base64Data, 
            style, 
            aspectRatio, 
            batchSize,
            settings,
            mimeType,
            (count) => setBatchProgress(count),
            model,
            customInstruction 
        );
        setBatchResults(results);
      }
    } catch (error) {
      console.error(error);
      alert("Generation failed. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const updateSetting = (category: keyof StudioSettings, key: string, value: string) => {
      setSettings(prev => ({
          ...prev,
          [category]: typeof prev[category] === 'object' 
            ? { ...prev[category] as any, [key]: value }
            : value
      }));
  };

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-5xl font-serif font-bold text-stone-900 mb-2 tracking-tight">Virtual Studio</h2>
          <p className="text-lg text-stone-500 font-light max-w-2xl leading-relaxed">
            {activeProduct ? <span>Shooting <span className="font-semibold text-stone-900">{activeProduct.name}</span></span> : "Configure your virtual photoshoot."}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Controls */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* 1. Main Configuration Card */}
          <div className="bg-white rounded-[2rem] p-6 shadow-xl shadow-stone-200/50 border border-stone-100">
             
             {/* Image Input Preview (Compact) */}
             <div 
                className={`w-full aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer mb-6 relative group border-2 ${!selectedImage ? 'border-dashed border-stone-200 bg-stone-50' : 'border-transparent'}`}
                onClick={() => fileInputRef.current?.click()}
             >
                {selectedImage ? (
                    <>
                        <img src={selectedImage} alt="Input" className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-xs font-bold uppercase tracking-wider">Change Source</span>
                        </div>
                    </>
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-stone-400">
                        <Camera className="w-8 h-8 mb-2 opacity-50" />
                        <span className="text-xs font-bold uppercase tracking-wide">Upload Product</span>
                    </div>
                )}
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
             </div>

             {/* Aspect Ratio */}
             <div className="mb-5">
                 <div className="flex bg-stone-100/80 p-1.5 rounded-2xl">
                     {['9:16', '3:4', '1:1', '16:9'].map(ratio => (
                         <button
                             key={ratio}
                             onClick={() => setAspectRatio(ratio)}
                             className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all duration-300 ${
                                 aspectRatio === ratio
                                 ? 'bg-white text-stone-900 shadow-md transform scale-[1.02]'
                                 : 'text-stone-500 hover:text-stone-700'
                             }`}
                         >
                             {ratio}
                         </button>
                     ))}
                 </div>
             </div>

             {/* Mode Switch */}
             <div className="mb-5">
                 <div className="flex bg-stone-100/80 p-1.5 rounded-2xl">
                    <button 
                        onClick={() => setMode('single')}
                        className={`flex-1 py-3 text-xs font-bold rounded-xl transition-all duration-300 ${mode === 'single' ? 'bg-white text-stone-900 shadow-md' : 'text-stone-500 hover:text-stone-700'}`}
                    >
                        Single Shot
                    </button>
                    <button 
                        onClick={() => setMode('batch')}
                        className={`flex-1 py-3 text-xs font-bold rounded-xl transition-all duration-300 ${mode === 'batch' ? 'bg-white text-stone-900 shadow-md' : 'text-stone-500 hover:text-stone-700'}`}
                    >
                        Story Batch
                    </button>
                 </div>
             </div>

             {/* Batch Size */}
             {mode === 'batch' && (
                <div className="flex items-center justify-between bg-stone-50 rounded-2xl p-4 border border-stone-100">
                    <span className="text-xs font-bold text-stone-500 uppercase tracking-wider pl-2">Batch Size</span>
                    <div className="flex items-center bg-white rounded-xl shadow-sm border border-stone-100 px-1 py-1">
                        <button onClick={() => setBatchSize(Math.max(2, batchSize - 1))} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-900 font-bold text-lg hover:bg-stone-50 rounded-lg transition-colors">-</button>
                        <span className="w-8 text-center text-sm font-bold text-stone-900">{batchSize}</span>
                        <button onClick={() => setBatchSize(Math.min(10, batchSize + 1))} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-900 font-bold text-lg hover:bg-stone-50 rounded-lg transition-colors">+</button>
                    </div>
                </div>
             )}
          </div>

          {/* 2. Advanced Settings Toggle */}
          <div className="py-2 border-t border-stone-200 border-dashed">
             <button 
                 onClick={() => setShowAdvanced(!showAdvanced)}
                 className="w-full flex items-center justify-center text-[10px] font-bold text-stone-400 uppercase tracking-widest hover:text-stone-600 transition-colors py-2 gap-2"
             >
                 <Sliders className="w-3 h-3" />
                 {showAdvanced ? 'Hide Advanced Configuration' : 'Advanced Configuration'}
             </button>
          </div>

          {/* 3. Advanced Settings Card */}
          {showAdvanced && (
             <div className="bg-white rounded-[2rem] p-6 shadow-xl shadow-stone-200/50 border border-stone-100 animate-in slide-in-from-top-4 duration-500">
                 
                 {/* Lighting Intensity */}
                 <div className="mb-8">
                     <div className="flex items-center gap-2 mb-4">
                         <Sun className="w-3 h-3 text-amber-500" />
                         <span className="text-xs font-bold text-stone-900 uppercase tracking-wider">Lighting Intensity</span>
                     </div>
                     <div className="grid grid-cols-2 gap-3">
                         {['soft', 'medium', 'hard', 'dramatic'].map(int => (
                             <button 
                                 key={int}
                                 onClick={() => updateSetting('lighting', 'intensity', int)}
                                 className={`py-3 px-4 rounded-xl text-[10px] font-bold uppercase tracking-wider border-2 transition-all duration-200 ${
                                     settings.lighting.intensity === int
                                     ? 'bg-amber-50 border-amber-200 text-amber-900 shadow-sm'
                                     : 'bg-white border-stone-100 text-stone-400 hover:border-stone-200 hover:text-stone-600'
                                 }`}
                             >
                                 {int}
                             </button>
                         ))}
                     </div>
                 </div>

                 {/* Camera Angle */}
                 <div className="mb-8">
                     <div className="flex items-center gap-2 mb-4">
                         <Aperture className="w-3 h-3 text-stone-900" />
                         <span className="text-xs font-bold text-stone-900 uppercase tracking-wider">Camera Angle</span>
                     </div>
                     <div className="grid grid-cols-3 gap-3">
                         {ANGLES.map(angle => {
                             const Icon = angle.icon;
                             const isSelected = settings.camera.angle === angle.id;
                             return (
                                 <button 
                                     key={angle.id}
                                     onClick={() => updateSetting('camera', 'angle', angle.id)}
                                     className={`flex flex-col items-center justify-center aspect-[4/3] rounded-2xl border-2 transition-all duration-300 ${
                                         isSelected
                                         ? 'bg-stone-900 border-stone-900 text-white shadow-lg transform scale-105'
                                         : 'bg-white border-stone-100 text-stone-400 hover:border-stone-200 hover:text-stone-600'
                                     }`}
                                 >
                                     <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-white' : 'text-stone-400'}`} />
                                     <span className="text-[9px] font-bold uppercase tracking-wide">{angle.label}</span>
                                 </button>
                             );
                         })}
                     </div>
                 </div>

                 {/* Custom Props */}
                 <div>
                      <div className="flex items-center gap-2 mb-4">
                         <Sparkles className="w-3 h-3 text-purple-500" />
                         <span className="text-xs font-bold text-stone-900 uppercase tracking-wider">Custom Props</span>
                     </div>
                     <input 
                         type="text" 
                         value={customInstruction} 
                         onChange={(e) => setCustomInstruction(e.target.value)}
                         placeholder="e.g. Include red roses, cinematic fog, luxury watch..."
                         className="w-full px-4 py-3 text-xs bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:border-stone-900 focus:bg-white transition-all placeholder:text-stone-400"
                     />
                 </div>
             </div>
          )}

          {/* 4. Action Button */}
          <Button 
              onClick={handleGenerate} 
              disabled={!selectedImage || isGenerating}
              isLoading={isGenerating}
              className="w-full py-5 rounded-2xl text-base shadow-2xl shadow-stone-900/30 bg-stone-900 hover:bg-black border-none"
              size="lg"
          >
              <Camera className="w-5 h-5 mr-3" />
              {mode === 'batch' ? `Create Storyboard (${batchSize})` : 'Generate Photography'}
          </Button>

        </div>

        {/* Right Column: Results */}
        <div className="lg:col-span-8">
            <div className="bg-[#101010] rounded-[2.5rem] min-h-[850px] flex flex-col p-8 relative overflow-hidden shadow-2xl shadow-stone-900/50 border border-stone-800">
                
                {/* Background ambient */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-stone-800/10 rounded-full blur-[100px] pointer-events-none"></div>

                {/* Content Area */}
                <div className="relative z-10 flex-1 flex flex-col">
                    
                    {/* Header inside Dark Mode */}
                    <div className="flex justify-between items-center mb-8 border-b border-white/5 pb-6">
                         <div className="flex items-center gap-3">
                             <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/10">
                                 {mode === 'batch' ? <Clapperboard className="w-5 h-5 text-white/60" /> : <Monitor className="w-5 h-5 text-white/60" />}
                             </div>
                             <div>
                                 <h3 className="text-white font-serif text-lg tracking-wide">{mode === 'batch' ? 'Campaign Storyboard' : 'Studio Output'}</h3>
                                 <p className="text-white/40 text-xs uppercase tracking-widest font-bold">
                                     {isGenerating ? 'Processing...' : (batchResults.length > 0 || resultImage ? 'Render Complete' : 'Waiting for Input')}
                                 </p>
                             </div>
                         </div>
                         {model === ImageModel.NANO_BANANA_PRO && (
                             <div className="px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-bold uppercase tracking-wide flex items-center gap-1.5">
                                 <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]"></div>
                                 Pro Model Active
                             </div>
                         )}
                    </div>

                    {/* RESULTS: BATCH MODE */}
                    {mode === 'batch' && (
                        <div className="flex-1">
                            {batchResults.length > 0 ? (
                                <div className="grid grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                    {batchResults.map((url, idx) => (
                                        <div key={idx} className="relative group rounded-3xl overflow-hidden aspect-[4/5] bg-stone-800 border border-white/5 shadow-2xl">
                                            <img src={url} alt={`Scene ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                            
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                                                 <div className="flex gap-3 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                                     <Button variant="white" size="sm" onClick={() => onSave(url, style)} className="flex-1 font-bold text-xs shadow-lg">
                                                         Save Asset
                                                     </Button>
                                                     <Button variant="glassDark" size="icon" onClick={() => window.open(url, '_blank')} className="rounded-xl">
                                                         <Download className="w-4 h-4" />
                                                     </Button>
                                                 </div>
                                            </div>

                                            <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-lg border border-white/10">
                                                <span className="text-white/90 text-[10px] font-bold uppercase tracking-wider">Scene {idx + 1}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : isGenerating ? (
                                <div className="h-full flex flex-col items-center justify-center">
                                     <div className="relative w-32 h-32 mb-8">
                                         <div className="absolute inset-0 border-t-2 border-stone-700 rounded-full animate-spin"></div>
                                         <div className="absolute inset-2 border-r-2 border-stone-600 rounded-full animate-spin reverse"></div>
                                         <div className="absolute inset-0 flex items-center justify-center">
                                             <Camera className="w-8 h-8 text-stone-500 animate-pulse" />
                                         </div>
                                     </div>
                                     <h3 className="text-xl text-white font-serif mb-2 animate-pulse">{batchProgress === 0 ? 'Planning Storyboard...' : `Rendering Scene ${batchProgress} of ${batchSize}`}</h3>
                                     <p className="text-stone-500 text-sm">AI is directing the photoshoot based on your props.</p>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                                    <div className="w-24 h-24 rounded-[2rem] bg-white/5 border border-white/5 flex items-center justify-center mb-6">
                                        <Layers className="w-10 h-10 text-white" />
                                    </div>
                                    <h3 className="text-2xl text-white font-serif mb-2">Ready to Shoot</h3>
                                    <p className="text-stone-400 max-w-sm leading-relaxed">Configured for {batchSize} unique storyboard shots using {model === ImageModel.NANO_BANANA_PRO ? 'Nano Banana Pro' : 'Nano Banana'}.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* RESULTS: SINGLE MODE */}
                    {mode === 'single' && (
                        <div className="flex-1 flex items-center justify-center">
                             {resultImage ? (
                                 <div className="relative w-full h-full max-h-[700px] flex flex-col">
                                     <img src={resultImage} alt="Result" className="w-full h-full object-contain rounded-lg shadow-2xl" />
                                     <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 bg-black/60 backdrop-blur-xl p-2 rounded-2xl border border-white/10">
                                         <Button variant="white" size="sm" onClick={() => onSave(resultImage, style)}>Save Asset</Button>
                                         <Button variant="glassDark" size="icon" onClick={() => setSelectedImage(null)}><RefreshCw className="w-4 h-4"/></Button>
                                     </div>
                                 </div>
                             ) : isGenerating ? (
                                <div className="flex flex-col items-center">
                                    <div className="w-16 h-16 border-4 border-stone-700 border-t-white rounded-full animate-spin mb-6"></div>
                                    <span className="text-stone-400 text-sm uppercase tracking-widest font-bold animate-pulse">Developing Photo...</span>
                                </div>
                             ) : (
                                <div className="flex flex-col items-center opacity-40">
                                    <div className="w-24 h-24 rounded-full border border-white/10 bg-white/5 flex items-center justify-center mb-6">
                                        <Camera className="w-10 h-10 text-white" />
                                    </div>
                                    <p className="text-stone-400 font-serif text-lg">Single Shot Mode</p>
                                </div>
                             )}
                        </div>
                    )}
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualStudio;
